import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Alert,
  AlertDescription,
  Flex,
  Grid,
  Spinner,
  Text,
} from '@kognitos/lattice';

import type { V1NodeExecutedOutputs } from '@/generated/hooks/bishop/models';
import { useGetExecutionOutputs } from '@/generated/hooks/jarvis/v1/api';
import { captureException } from '@/modules/sentry';
import { AccessDeniedAlert } from '@/shared/components';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';
import { checkIsAccessDeniedError } from '@/shared/utils/error-handler';

import { OutputCard } from './OutputCard';
import { PreviewLayover } from './PreviewLayover';
import type { TResolvedValue } from '../types';
import {
  applyDisplayFilters,
  categorizeOutputsForGrid,
  extractOutputsValue,
  isDictionary,
  isPrimitiveLong,
  isFile,
  isTable,
  getResolvedFileDisplayName,
} from '../utils';
import { downloadOutput } from '../utils/download';

interface IExecutionOutputsProps {
  /** The jarvis execution whose `set_output()` values to show. */
  executionId: string;
  variant?: 'cards' | 'compact';
}

/**
 * An execution's outputs as the shared output cards. Takes an id rather than a
 * values map because cross-stored tables and files reach a caller only as a
 * reference; the fetched values are what `OutputCard` can actually draw.
 * Renders nothing when there is nothing to show.
 */
export function ExecutionOutputs({
  executionId,
  variant = 'cards',
}: IExecutionOutputsProps) {
  const { t } = useTranslation('outputs');
  const organizationId = useUserPreferencesStore(selectCurrentOrganizationId);
  const workspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);

  const [previewData, setPreviewData] = useState<{
    name: string;
    value: TResolvedValue;
  } | null>(null);

  const {
    data: response,
    isLoading,
    isError,
    error,
  } = useGetExecutionOutputs(organizationId, workspaceId, executionId, {
    query: {
      enabled: Boolean(executionId && organizationId && workspaceId),
      // A finished execution's outputs are immutable.
      staleTime: 5 * 60 * 1000,
      meta: {
        onError: (queryError: Error) => {
          captureException(queryError, {
            tags: { module: 'outputs', action: 'fetchExecutionOutputs' },
          });
        },
      },
    },
  });

  const entries = useMemo(() => {
    const outputs = response?.data?.outputs;
    if (!outputs) return null;
    // jarvis wraps each output as `{ value, type }`; the resolver takes bare values.
    const values = Object.fromEntries(
      Object.entries(outputs).map(([name, output]) => [
        name,
        output.value ?? {},
      ]),
    ) as V1NodeExecutedOutputs;
    const resolved = applyDisplayFilters(extractOutputsValue(values)).outputs;
    // Sorted because a protobuf map carries no order guarantee.
    const entries = Object.entries(resolved);
    if (
      variant === 'compact' &&
      entries.length === 1 &&
      isDictionary(entries[0][1])
    ) {
      return Object.entries(entries[0][1]).sort(([a], [b]) =>
        a.localeCompare(b),
      );
    }
    return entries.sort(([a], [b]) => a.localeCompare(b));
  }, [response, variant]);

  const handleExpand = useCallback((name: string, value: TResolvedValue) => {
    setPreviewData({ name, value });
  }, []);

  const handleDownload = useCallback((name: string, value: TResolvedValue) => {
    downloadOutput(value, name);
  }, []);

  if (isLoading) {
    return (
      <Flex align="center" gap={2} data-testid="execution-outputs-loading">
        <Spinner size="sm" />
        <Text color="muted" level="small">
          {t('outputs.execution.loading')}
        </Text>
      </Flex>
    );
  }

  if (isError) {
    return checkIsAccessDeniedError(error) ? (
      <AccessDeniedAlert />
    ) : (
      <Alert variant="destructive" data-testid="execution-outputs-error">
        <AlertDescription>{t('outputs.execution.error')}</AlertDescription>
      </Alert>
    );
  }

  const {
    largeMedia = [],
    verticalCards = [],
    horizontalCards = [],
  } = categorizeOutputsForGrid(entries ?? [], true);
  if (!largeMedia.length && !verticalCards.length && !horizontalCards.length) {
    return null;
  }

  const renderZone = (
    cards: [string, TResolvedValue][],
    { horizontal, testId }: { horizontal: boolean; testId: string },
  ) => (
    // A fixed count, not a responsive one: `sm:`/`lg:` answer the viewport, and
    // this renders in a narrow panel where they resolved to unreadable columns.
    <Grid
      cols={horizontal ? 2 : 1}
      gap={3}
      className="w-full"
      data-testid={testId}
    >
      {cards.map(([key, value], index) => (
        <Flex
          key={key}
          direction="column"
          // `Flex` aligns to `start` by default, which collapses the card to its
          // content width; a grid cell wants it stretched across the track.
          align="stretch"
          width="full"
          // A lone trailing primitive takes both tracks rather than leaving a hole.
          className={
            horizontal && index === cards.length - 1 && cards.length % 2 === 1
              ? 'col-span-2'
              : undefined
          }
        >
          <OutputCard
            name={key}
            value={value}
            horizontal={horizontal}
            onExpand={handleExpand}
            onDownload={handleDownload}
          />
        </Flex>
      ))}
    </Grid>
  );

  return (
    <>
      <Flex
        direction="column"
        gap={3}
        width="full"
        data-testid="execution-outputs"
      >
        {variant === 'compact' &&
          entries?.map(([name, value]) => (
            <Grid key={name} cols={2} gap={3} className="w-full">
              <Text
                level="small"
                color="muted"
                className="first-letter:uppercase break-words"
              >
                {name.replace(/_/g, ' ')}
              </Text>
              {value !== null &&
              (typeof value === 'object' ||
                isPrimitiveLong(value) ||
                (typeof value === 'string' &&
                  ['{', '['].includes(value.trim()[0]))) ? (
                <Button
                  variant="link"
                  size="sm"
                  className="h-auto justify-start p-0 text-informative whitespace-normal"
                  onClick={() => handleExpand(name, value)}
                >
                  {isFile(value)
                    ? getResolvedFileDisplayName(value)
                    : t(
                        isTable(value)
                          ? 'outputs.execution.viewTable'
                          : 'outputs.execution.viewDetails',
                      )}
                </Button>
              ) : (
                <Text level="small" className="break-words">
                  {value ?? '—'}
                </Text>
              )}
            </Grid>
          ))}
        {variant === 'cards' &&
          horizontalCards.length > 0 &&
          renderZone(horizontalCards, {
            horizontal: true,
            testId: 'zone-horizontal',
          })}

        {variant === 'cards' &&
          verticalCards.length > 0 &&
          renderZone(verticalCards, {
            horizontal: false,
            testId: 'zone-vertical',
          })}

        {variant === 'cards' &&
          largeMedia.length > 0 &&
          renderZone(largeMedia, {
            horizontal: false,
            testId: 'zone-large-media',
          })}
      </Flex>

      {previewData && (
        <PreviewLayover
          isOpen
          outputName={previewData.name}
          outputValue={previewData.value}
          onClose={() => setPreviewData(null)}
          onDownload={downloadOutput}
        />
      )}
    </>
  );
}
